import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { ACCOUNT_TYPE_CODE } from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { ChangeRequestTypeEnum } from 'src/constants/change-request';
import { ResponseDTO } from 'src/dto/base.dto';
import {
	CreateOrUpdateChangeChangeRequestDto,
	DeleteChangeRequestDto,
	FileDeleteFileDTO,
	GetChangeChangeRequestDto,
	GetExistingDto
} from 'src/dto/change-request.dto';
import { uniqueKey } from 'src/helpers/string.utils';
import {
	IChangeRequest,
	IChangeRequestBody,
	IChangeRequestExisting,
	IUploadDocumentResponse
} from 'src/interfaces/change-request';
import i18n, { i18nKey } from 'src/locales/i18n';
import { IHttpService } from 'src/services/http.service';
import { IUserStore } from '../user';

export interface IChangeRequestStore {
	changeRequest: IChangeRequest[];
	selectedChangeRequestType: ChangeRequestTypeEnum[];
	get crTypeOptions(): Array<{
		key: ChangeRequestTypeEnum;
		value: ChangeRequestTypeEnum;
		label: string;
		disabled: boolean;
	}>;
	initChangeRequest(): void;
	loadChangeRequest(): Promise<boolean>;
	submitChangeRequests(changeRequest: IChangeRequest[]): Promise<boolean>;
	addChangeRequest(changeRequest: IChangeRequest): void;
	addSelectedType(id: string, type: ChangeRequestTypeEnum): void;
	deleteCrById(internalId: string): Promise<boolean>;
	dispose(): void;
	getExisting(accountId: string, type: ChangeRequestTypeEnum): Promise<IChangeRequestExisting>;
	uploadFile(internalId: string, form: FormData): Promise<string | undefined>;
	downloadFile(fileId: string, fileName: string): Promise<void>;
	deleteFile(fileId: string, internalId: string): Promise<boolean>;
	get disabledAddMoreCr(): boolean;
	get getTotalFilesSize(): number;
}

export class ChangeRequestStore implements IChangeRequestStore {
	changeRequest: IChangeRequest[] = [
		{
			type: ChangeRequestTypeEnum.BankAccountDetails,
			internalId: uniqueKey(10)
		} as IChangeRequest
	];

	selectedChangeRequestType: ChangeRequestTypeEnum[] = [ChangeRequestTypeEnum.BankAccountDetails];

	constructor(private readonly http: IHttpService, private readonly userStore: IUserStore) {
		makeAutoObservable(this, {
			changeRequest: observable,
			addChangeRequest: action,
			addSelectedType: action,
			crTypeOptions: computed,
			selectedChangeRequestType: observable,
			deleteCrById: action,
			dispose: action,
			disabledAddMoreCr: computed
		});
	}
	public initChangeRequest(): void {
		runInAction(() => {
			this.changeRequest = [
				{
					type: ChangeRequestTypeEnum.BankAccountDetails,
					internalId: uniqueKey(10)
				} as IChangeRequest
			]
			this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);
		})
	}
	public async loadChangeRequest(): Promise<boolean> {
		const dto = new GetChangeChangeRequestDto();
		const res: ResponseDTO<IChangeRequest[]> = await this.http.request<GetChangeChangeRequestDto, IChangeRequest[]>(
			dto
		);
		if (res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				const responseData =
					res.data && res.data.length
						? res.data
						: [
							{
								type: ChangeRequestTypeEnum.BankAccountDetails
							} as IChangeRequest
						];
				this.changeRequest = responseData.map((c) => {
					return {
						...c,
						internalId: uniqueKey(10)
					};
				});
				this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);
			});
			return true;
		}
		return false;
	}

	public async submitChangeRequests(changeRequests: IChangeRequest[]): Promise<boolean> {
		const changeRequestDto: IChangeRequestBody = {
			data: changeRequests
		};
		const dto = new CreateOrUpdateChangeChangeRequestDto(changeRequestDto);
		const rs: ResponseDTO<IChangeRequest[]> = await this.http.request<
			CreateOrUpdateChangeChangeRequestDto,
			IChangeRequest[]
		>(dto);
		if (rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.changeRequest = [
					{
						type: ChangeRequestTypeEnum.BankAccountDetails,
						internalId: uniqueKey(10)
					} as IChangeRequest
				]
				this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);
			})
			// runInAction(() => {
			// 	const responseData = rs.data || [];
			// 	this.changeRequest = responseData.reverse().map((c) => {
			// 		return {
			// 			...c,
			// 			internalId: uniqueKey(10)
			// 		};
			// 	});
			// 	this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);
			// });
			return true;
		}
		return false;
	}

	public addChangeRequest(changeRequest: IChangeRequest) {
		if (!this.changeRequest) {
			this.changeRequest = [changeRequest];
		} else {
			this.changeRequest.push(changeRequest);
		}

		this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);
	}

	public addSelectedType(id: string, type: ChangeRequestTypeEnum) {
		if (!this.changeRequest) {
			return;
		}

		this.changeRequest = this.changeRequest.map((c) => {
			if (c.internalId == id) {
				c.type = type;
			}
			return c;
		});
		this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);
	}

	public get crTypeOptions(): Array<{
		key: ChangeRequestTypeEnum;
		value: ChangeRequestTypeEnum;
		label: string;
		disabled: boolean;
	}> {
		const baseSelectOptions = [
			{
				key: ChangeRequestTypeEnum.EnglishName,
				value: ChangeRequestTypeEnum.EnglishName,
				label: i18n.t(i18nKey.crPage.crTypes.en),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.EnglishName)
			},
			{
				key: ChangeRequestTypeEnum.ChinesName,
				value: ChangeRequestTypeEnum.ChinesName,
				label: i18n.t(i18nKey.crPage.crTypes.cn),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.ChinesName)
			},
			{
				key: ChangeRequestTypeEnum.ContactNumber,
				value: ChangeRequestTypeEnum.ContactNumber,
				label: i18n.t(i18nKey.crPage.crTypes.contactNumber),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.ContactNumber)
			},
			{
				key: ChangeRequestTypeEnum.EmailAddress,
				value: ChangeRequestTypeEnum.EmailAddress,
				label: i18n.t(i18nKey.crPage.crTypes.emailAddr),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.EmailAddress)
			},
			{
				key: ChangeRequestTypeEnum.ResidentialAddress,
				value: ChangeRequestTypeEnum.ResidentialAddress,
				label: i18n.t(i18nKey.crPage.crTypes.residentialAddr),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.ResidentialAddress)
			},
			{
				key: ChangeRequestTypeEnum.MailingAddress,
				value: ChangeRequestTypeEnum.MailingAddress,
				label: i18n.t(i18nKey.crPage.crTypes.mailingAddr),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.MailingAddress)
			},
			{
				key: ChangeRequestTypeEnum.Nationality,
				value: ChangeRequestTypeEnum.Nationality,
				label: i18n.t(i18nKey.crPage.crTypes.nationality),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.Nationality)
			},
			{
				key: ChangeRequestTypeEnum.BankAccountDetails,
				value: ChangeRequestTypeEnum.BankAccountDetails,
				label: i18n.t(i18nKey.crPage.crTypes.bankAccount),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.BankAccountDetails)
			},
			{
				key: ChangeRequestTypeEnum.CountryOfTaxResidence,
				value: ChangeRequestTypeEnum.CountryOfTaxResidence,
				label: i18n.t(i18nKey.crPage.crTypes.countryOfTax),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.CountryOfTaxResidence)
			},
			{
				key: ChangeRequestTypeEnum.SourceOfFund,
				value: ChangeRequestTypeEnum.SourceOfFund,
				label: i18n.t(i18nKey.crPage.crTypes.sourceOfFund),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.SourceOfFund)
			},
			{
				key: ChangeRequestTypeEnum.EmploymentDetails,
				value: ChangeRequestTypeEnum.EmploymentDetails,
				label: i18n.t(i18nKey.crPage.crTypes.employmentDetails),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.EmploymentDetails)
			}
		];

		const corporateSelectOptions = [
			{
				key: ChangeRequestTypeEnum.EnglishName,
				value: ChangeRequestTypeEnum.EnglishName,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.en),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.EnglishName)
			},
			{
				key: ChangeRequestTypeEnum.ChinesName,
				value: ChangeRequestTypeEnum.ChinesName,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.cn),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.ChinesName)
			},
			{
				key: ChangeRequestTypeEnum.ContactNumber,
				value: ChangeRequestTypeEnum.ContactNumber,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.contactNumber),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.ContactNumber)
			},
			{
				key: ChangeRequestTypeEnum.EmailAddress,
				value: ChangeRequestTypeEnum.EmailAddress,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.emailAddr),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.EmailAddress)
			},
			{
				key: ChangeRequestTypeEnum.ResidentialAddress,
				value: ChangeRequestTypeEnum.ResidentialAddress,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.residentialAddr),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.ResidentialAddress)
			},
			{
				key: ChangeRequestTypeEnum.MailingAddress,
				value: ChangeRequestTypeEnum.MailingAddress,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.mailingAddr),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.MailingAddress)
			},
			{
				key: ChangeRequestTypeEnum.Nationality,
				value: ChangeRequestTypeEnum.Nationality,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.nationality),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.Nationality)
			},
			{
				key: ChangeRequestTypeEnum.BankAccountDetails,
				value: ChangeRequestTypeEnum.BankAccountDetails,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.bankAccount),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.BankAccountDetails)
			},
			{
				key: ChangeRequestTypeEnum.CountryOfTaxResidence,
				value: ChangeRequestTypeEnum.CountryOfTaxResidence,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.countryOfTax),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.CountryOfTaxResidence)
			},
			{
				key: ChangeRequestTypeEnum.SourceOfFund,
				value: ChangeRequestTypeEnum.SourceOfFund,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.sourceOfFund),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.SourceOfFund)
			},
			{
				key: ChangeRequestTypeEnum.CompanyName,
				value: ChangeRequestTypeEnum.CompanyName,
				label: i18n.t(i18nKey.crPage.corporateCrTypes.companyName),
				disabled: this.selectedChangeRequestType.includes(ChangeRequestTypeEnum.CompanyName)
			}
		];

		const selectOptions = this.userStore.accountType == ACCOUNT_TYPE_CODE.CORPORATE ?
			corporateSelectOptions : baseSelectOptions

		return selectOptions.sort((a, b) => a.label.localeCompare(b.label));
	}

	public async deleteCrById(internalId: string): Promise<boolean> {
		if (!this.changeRequest) {
			return true;
		}
		const cloned = this.changeRequest.slice();

		const idx = cloned.findIndex((c) => c.internalId == internalId, 0);

		if (idx > -1) {
			const removeItem = cloned[idx];
			if (removeItem.id) {
				const removeItemDto = new DeleteChangeRequestDto({
					crId: removeItem.id
				});

				const rs: ResponseDTO<boolean> = await this.http.request<DeleteChangeRequestDto, boolean>(removeItemDto);
				if (rs.responseKey != HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
					return false;
				}
			}

			cloned.splice(idx, 1);
		}

		this.changeRequest = cloned.map((c) => c);

		this.selectedChangeRequestType = this.changeRequest.map((c) => c.type);

		return true;
	}

	public dispose(): void {
		this.selectedChangeRequestType = [];
		this.changeRequest = [];
	}

	public async getExisting(accountId: string, type: ChangeRequestTypeEnum): Promise<IChangeRequestExisting> {
		const dto = new GetExistingDto({
			accountId,
			type
		});
		const res: ResponseDTO<IChangeRequestExisting> = await this.http.request<GetExistingDto, IChangeRequestExisting>(
			dto
		);
		if (res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return res.data || { existing: {} };
		}
		return { existing: {} };
	}

	public async uploadFile(internalId: string, form: FormData): Promise<string | undefined> {
		const res: ResponseDTO<IUploadDocumentResponse> = await this.http.uploadFile(
			ENDPOINT.CHANGE_REQUEST_FILE_UPLOAD,
			form
		);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				const cloned = this.changeRequest.slice();

				const idx = cloned.findIndex((c) => c.internalId == internalId, 0);

				if (idx > -1 && res.data) {
					const existing = cloned[idx];
					(existing.id = res.data.id),
						(existing.type = res.data.type),
						(existing.documents = [res.data.document || {}]);
				}

				this.changeRequest = cloned.map((c) => c);
			});
			return res.data?.id;
		}
		return;
	}

	public async downloadFile(fileId: string, fileName: string): Promise<void> {
		await this.http.downloadFile(
			ENDPOINT.CHANGE_REQUEST_FILE_DOWNLOAD.concat(fileId),
			{},
			{
				name: fileName || ''
			}
		);
	}

	public async deleteFile(fileId: string, internalId: string): Promise<boolean> {
		const param = {
			documentId: fileId
		};

		const dto = new FileDeleteFileDTO(param);
		const res: ResponseDTO<string> = await this.http.request<FileDeleteFileDTO, string>(dto);

		if (res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				const cloned = this.changeRequest.slice();

				const idx = cloned.findIndex((c) => c.internalId == internalId, 0);

				if (idx > -1) {
					const existing = cloned[idx];
					existing.documents = undefined;
				}

				this.changeRequest = cloned.map((c) => c);
			});

			return true;
		}
		return false;
	}
	get getTotalFilesSize(): number {
		return 0;
	}

	public get disabledAddMoreCr(): boolean {
		if (this.userStore.accountType == ACCOUNT_TYPE_CODE.CORPORATE) {
			return this.selectedChangeRequestType.length >= 12;
		}
		return this.selectedChangeRequestType.length >= 11;
	}
}
